<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h3>👁️ Détails du Prospect</h3>
          <p class="prospect-id">ID: {{ prospect?._id || 'N/A' }}</p>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Informations Client -->
        <div class="section" :class="{ 'section-collapsed': !expandedSections.client }">
          <div class="section-header" @click="toggleSection('client')">
            <div class="section-icon">
              <i class="fas fa-user"></i>
            </div>
            <h4>👤 Informations Client</h4>
            <i class="fas fa-chevron-down section-toggle-icon"></i>
          </div>
          <div class="section-content" v-show="expandedSections.client">
            <div class="info-grid">
              <div class="info-item">
                <label>Nom Complet</label>
                <div class="info-value">{{ prospect?.client?.nomComplet || '-' }}</div>
              </div>
              <div class="info-item">
                <label>Téléphone</label>
                <div class="info-value">
                  <i class="fas fa-phone"></i>
                  {{ prospect?.client?.telephone || '-' }}
                </div>
              </div>
              <div class="info-item">
                <label>Email</label>
                <div class="info-value">
                  <i class="fas fa-envelope"></i>
                  {{ prospect?.client?.email || '-' }}
                </div>
              </div>
              <div class="info-item full-width">
                <label>Adresse</label>
                <div class="info-value">
                  <i class="fas fa-map-marker-alt"></i>
                  {{ formatAddress(prospect?.client?.adresse) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails Intervention -->
        <div class="section" :class="{ 'section-collapsed': !expandedSections.intervention }">
          <div class="section-header" @click="toggleSection('intervention')">
            <div class="section-icon">
              <i class="fas fa-tools"></i>
            </div>
            <h4>🔧 Détails de l'Operation</h4>
            <i class="fas fa-chevron-down section-toggle-icon"></i>
          </div>
          <div class="section-content" v-show="expandedSections.intervention">
            <div class="info-grid">
              <div class="info-item">
                <label>Type d'intervention</label>
                <div class="info-value">
                  <span class="type-badge">
                    {{ getTypeIcon(prospect?.typeIntervention) }} {{ prospect?.typeIntervention || '-' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <label>Priorité</label>
                <div class="info-value">
                  <span :class="getPrioriteBadgeClass(prospect?.priorite)">
                    {{ getPrioriteIcon(prospect?.priorite) }} {{ prospect?.priorite || '-' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <label>Prix</label>
                <div class="info-value">
                  <span class="price-badge">
                    <i class="fas fa-coins"></i>
                    {{ formatPrice(prospect?.prix) }}
                  </span>
                </div>
              </div>
              
              <!-- Liste des appareils demandés -->
              <div v-if="prospect?.appareils && prospect.appareils.length > 0" class="info-item full-width">
                <label>
                  <i class="fas fa-list"></i>
                  Appareils demandés ({{ getTotalAppareils() }})
                </label>
                <div class="appareils-display">
                  <div 
                    v-for="(appareil, index) in prospect.appareils" 
                    :key="index"
                    class="appareil-chip"
                  >
                    <span class="appareil-icon">{{ getAppareilIcon(appareil.type) }}</span>
                    <span class="appareil-name">{{ appareil.type }}</span>
                    <span class="appareil-qty">× {{ appareil.quantite }}</span>
                  </div>
                </div>
              </div>
              
              <!-- Affichage ancien format (si pas de liste d'appareils) -->
              <template v-else>
                <div class="info-item">
                  <label>Type d'appareil</label>
                  <div class="info-value">
                    {{ getAppareilIcon(prospect?.typeAppareil) }} {{ prospect?.typeAppareil || '-' }}
                  </div>
                </div>
                <div class="info-item">
                  <label>Nombre d'appareils</label>
                  <div class="info-value">
                    <i class="fas fa-microchip"></i>
                    {{ prospect?.nombreAppareils || 0 }}
                  </div>
                </div>
              </template>

              <div v-if="prospect?.description" class="info-item full-width">
                <label>Description</label>
                <div class="info-value description-text">
                  {{ prospect.description }}
                </div>
              </div>

              <div v-if="prospect?.commentaires" class="info-item full-width">
                <label>Commentaires</label>
                <div class="info-value description-text">
                  {{ prospect.commentaires }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Statut et Affectation -->
        <div class="section" :class="{ 'section-collapsed': !expandedSections.statut }">
          <div class="section-header" @click="toggleSection('statut')">
            <div class="section-icon">
              <i class="fas fa-flag"></i>
            </div>
            <h4>📊 Statut et Affectation</h4>
            <i class="fas fa-chevron-down section-toggle-icon"></i>
          </div>
          <div class="section-content" v-show="expandedSections.statut">
            <div class="info-grid">
              <div class="info-item">
                <label>Statut</label>
                <div class="info-value">
                  <span :class="getStatutBadgeClass(prospect?.statut)">
                    {{ getStatutIcon(prospect?.statut) }} {{ prospect?.statut || '-' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <label>Technicien affecté</label>
                <div class="info-value">
                  <i class="fas fa-user-cog"></i>
                  <template v-if="prospect?.technicienAffecte">
                    <template v-if="typeof prospect.technicienAffecte === 'object'">
                      {{ prospect.technicienAffecte.prenom }} {{ prospect.technicienAffecte.nom }}
                    </template>
                    <template v-else>
                      {{ getTechnicienName(prospect.technicienAffecte) }}
                    </template>
                  </template>
                  <span v-else class="text-muted">Aucun technicien affecté</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Créneaux Proposés -->
        <div class="section" :class="{ 'section-collapsed': !expandedSections.creneaux }">
          <div class="section-header" @click="toggleSection('creneaux')">
            <div class="section-icon">
              <i class="fas fa-calendar"></i>
            </div>
            <h4>📅 Créneaux Proposés</h4>
            <i class="fas fa-chevron-down section-toggle-icon"></i>
          </div>
          <div class="section-content" v-show="expandedSections.creneaux">
            <div v-if="prospect?.datesProposes && prospect.datesProposes.length > 0" class="dates-list">
              <div 
                v-for="(date, index) in prospect.datesProposes" 
                :key="index"
                class="date-card"
              >
                <div class="date-main">
                  <i class="fas fa-calendar-day"></i>
                  <strong>{{ formatDate(date?.date) }}</strong>
                </div>
                <div class="date-time">
                  <i class="fas fa-clock"></i>
                  {{ date?.heureDebut }} - {{ date?.heureFin }}
                </div>
                <div class="date-status" :class="{ 'available': date?.disponible }">
                  <i :class="date?.disponible ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                  {{ date?.disponible ? 'Disponible' : 'Non disponible' }}
                </div>
              </div>
            </div>
            <div v-else class="no-dates-info">
              <i class="fas fa-calendar-times"></i>
              <p>Aucun créneau proposé</p>
            </div>
          </div>
        </div>

        <!-- Informations Système -->
        <div class="section" :class="{ 'section-collapsed': !expandedSections.systeme }">
          <div class="section-header" @click="toggleSection('systeme')">
            <div class="section-icon">
              <i class="fas fa-info-circle"></i>
            </div>
            <h4>ℹ️ Informations Système</h4>
            <i class="fas fa-chevron-down section-toggle-icon"></i>
          </div>
          <div class="section-content" v-show="expandedSections.systeme">
            <div class="info-grid">
              <div class="info-item">
                <label>Date de création</label>
                <div class="info-value">
                  <i class="fas fa-calendar-plus"></i>
                  {{ formatDateTime(prospect?.dateCreation) }}
                </div>
              </div>
              <div v-if="prospect?.dateModification" class="info-item">
                <label>Dernière modification</label>
                <div class="info-value">
                  <i class="fas fa-edit"></i>
                  {{ formatDateTime(prospect?.dateModification) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn btn-secondary">
          <i class="fas fa-times"></i>
          Fermer
        </button>
        
        <button 
          v-if="canEdit"
          @click="openEditModal" 
          class="btn btn-primary"
        >
          <i class="fas fa-edit"></i>
          Modifier
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

import { API_BASE_URL } from '@/config/api'

export default {
  name: 'ProspectViewModal',
  props: {
    prospect: {
      type: Object,
      required: true
    },
    userRole: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'edit'],
  setup(props, { emit }) {
    const techniciens = ref([])
    
    // État des sections (toutes fermées par défaut)
    const expandedSections = ref({
      client: false,
      intervention: false,
      statut: false,
      creneaux: false,
      systeme: false
    })

    // Fonction pour basculer une section
    const toggleSection = (sectionName) => {
      expandedSections.value[sectionName] = !expandedSections.value[sectionName]
    }

    // Permissions basées sur le rôle
    const canEdit = computed(() => {
      return props.userRole === 'Admin' || props.userRole === 'Responsable Planning'
    })

    // Charger la liste des techniciens
    const loadTechniciens = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`${API_BASE_URL}/api/prospects/techniciens`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          techniciens.value = result.techniciens || []
        }
      } catch (error) {
        console.error('Erreur lors du chargement des techniciens:', error)
      }
    }

    const closeModal = () => {
      emit('close')
    }

    const openEditModal = () => {
      emit('edit', props.prospect)
    }

    // Utilitaires d'affichage
    const getStatutBadgeClass = (statut) => {
      const classes = {
        'En attente': 'badge badge-warning',
        'Validé': 'badge badge-success',
        'Rejeté': 'badge badge-danger',
        'Planifié': 'badge badge-info'
      }
      return classes[statut] || 'badge badge-secondary'
    }

    const getStatutIcon = (statut) => {
      const icons = {
        'En attente': '⏳',
        'Validé': '✅',
        'Rejeté': '❌',
        'Planifié': '📅'
      }
      return icons[statut] || '📋'
    }

    const getTypeIcon = (type) => {
      const icons = {
        'Installation': '🔧',
        'Intervention': '⚙️',
        'Prospect': '💼'
      }
      return icons[type] || '📋'
    }

    const getPrioriteBadgeClass = (priorite) => {
      const classes = {
        'Basse': 'badge badge-success',
        'Normale': 'badge badge-info',
        'Haute': 'badge badge-warning',
        'Urgente': 'badge badge-danger'
      }
      return classes[priorite] || 'badge badge-secondary'
    }

    const getPrioriteIcon = (priorite) => {
      const icons = {
        'Basse': '🟢',
        'Normale': '🟡',
        'Haute': '🟠',
        'Urgente': '🔴'
      }
      return icons[priorite] || '⚪'
    }

    const getTechnicienName = (technicienId) => {
      if (!technicienId) return null
      const technicien = techniciens.value.find(t => t._id === technicienId)
      return technicien ? `${technicien.prenom} ${technicien.nom}` : 'Technicien inconnu'
    }

    const formatAddress = (address) => {
      if (!address) return 'Non renseignée'
      
      if (typeof address === 'string') {
        return address
      }
      
      if (typeof address === 'object') {
        const parts = []
        if (address.rue) parts.push(address.rue)
        if (address.ville) parts.push(address.ville)
        if (address.codePostal) parts.push(address.codePostal)
        if (address.region) parts.push(address.region)
        return parts.length > 0 ? parts.join(', ') : 'Adresse incomplète'
      }
      
      return 'Format d\'adresse invalide'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getAppareilIcon = (type) => {
      const icons = {
        'GPS': '📡',
        'SIM': '📱',
        'Cable': '🔌',
        'Boitier': '📦',
        'Antenne': '📶',
        'Capteur': '🔍'
      }
      return icons[type] || '📦'
    }

    const getTotalAppareils = () => {
      if (!props.prospect?.appareils || props.prospect.appareils.length === 0) {
        return 0
      }
      return props.prospect.appareils.reduce((total, app) => total + app.quantite, 0)
    }

    const formatPrice = (price) => {
      if (price === null || price === undefined) return 'Non renseigné'
      return `${Number(price).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TND`
    }

    onMounted(() => {
      loadTechniciens()
    })

    return {
      expandedSections,
      toggleSection,
      canEdit,
      closeModal,
      openEditModal,
      getStatutBadgeClass,
      getStatutIcon,
      getTypeIcon,
      getPrioriteBadgeClass,
      getPrioriteIcon,
      getTechnicienName,
      formatAddress,
      formatDate,
      formatDateTime,
      getAppareilIcon,
      getTotalAppareils,
      formatPrice
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 1000px;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: none;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border-radius: 24px 24px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent);
  pointer-events: none;
}

.header-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.prospect-id {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
}

.section {
  margin-bottom: 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-left: 4px solid var(--primary-blue);
  overflow: hidden;
  transition: all 0.3s ease;
}

.section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 27, 183, 0.3);
}

.section-collapsed {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-collapsed:hover {
  transform: translateY(0);
}

.section-header {
  display: flex;
  cursor: pointer;
  user-select: none;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  position: relative;
}

.section-header:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08), rgba(0, 70, 255, 0.04));
}

.section-header h4 {
  flex: 1;
}

.section-toggle-icon {
  margin-left: auto;
  color: var(--primary-blue);
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.section-collapsed .section-toggle-icon {
  transform: rotate(-90deg);
}

.section-content {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.section-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.section-content {
  padding: 1.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(248, 250, 252, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  font-weight: 500;
  color: #1e293b;
  min-height: 45px;
}

.info-value i {
  color: var(--secondary-blue);
  font-size: 0.9rem;
}

.description-text {
  white-space: pre-wrap;
  line-height: 1.6;
  display: block;
  max-height: 150px;
  overflow-y: auto;
  word-wrap: break-word;
  word-break: break-word;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.badge-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.badge-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.badge-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.badge-secondary {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.75rem;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2563eb;
}

.price-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1));
  border: 2px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  color: #059669;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
  transition: all 0.3s ease;
}

.price-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.price-badge i {
  color: #10b981;
  font-size: 1.1rem;
}

/* Appareils */
.appareils-display {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.appareil-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
  border: 2px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.appareil-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.appareil-icon {
  font-size: 1.2rem;
}

.appareil-name {
  color: #059669;
  font-size: 0.9rem;
}

.appareil-qty {
  color: #6b7280;
  font-size: 0.85rem;
  padding-left: 0.5rem;
  border-left: 1px solid rgba(16, 185, 129, 0.3);
}

/* Créneaux */
.dates-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.date-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
}

.date-card:hover {
  border-color: rgba(0, 27, 183, 0.2);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.08);
}

.date-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
  flex: 1;
}

.date-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
  flex: 1;
}

.date-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.4rem 0.75rem;
  border-radius: 10px;
}

.date-status.available {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.date-status:not(.available) {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.no-dates-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
}

.no-dates-info i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-dates-info p {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(15px);
  border-radius: 0 0 24px 24px;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:hover::before {
  left: 100%;
}

.text-muted {
  color: #9ca3af;
  font-style: italic;
}

.btn-secondary {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
  box-shadow: 0 4px 15px rgba(107, 114, 128, 0.3);
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.4);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    margin: 0.5rem;
    max-height: 98vh;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 1.5rem;
    border-radius: 16px 16px 0 0;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .date-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .modal-footer {
    padding: 1.5rem;
    flex-direction: column;
    gap: 1rem;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
